(ns practitest.frontend.components2.spinners
  (:require [practitest.frontend.pt-ts-bridge :as pt-ts :refer [Loading]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.utils :refer [development-only example register-components!]]
            [reagent.core :as reagent]))


(defn ^{:component? true} delayed-after-mount
  "Higher order component to add delay before mounting another component.

  Right now used with spinners, but theoretically can be used with any component.

  * `delay` - delay in milliseconds before mounting, default is 0, if 0 - mounted instantly (no delay)
  * `component-fn` - `(fn [delay-finished])` - callback that will be passed a
                     boolean indicating whether delay is finished or not"
  [delay component-fn]
  (let [delay (or delay 0)
        delay-finished (reagent/atom (if (= 0 delay)
                                       true
                                       false))
        timeout-id (atom nil)]
    (reagent/create-class
      {:component-did-mount    (fn [_]
                                 (when (> delay 0)
                                   (reset! timeout-id
                                           (js/setTimeout
                                             (fn []
                                               (reset! delay-finished true))
                                             delay))))
       :component-will-unmount (fn [_]
                                 (when-let [id @timeout-id]
                                   (js/clearTimeout id)))
       :reagent-render         (fn [_ component-fn]
                                 (component-fn @delay-finished))})))

;;
;; Design criterias for spinners
;;
;; 1. We want to show some kind of progress indicator when network operation is performed,
;;    otherwise it's not clear if UI is stuck or if something is going on
;; 2. We don't want to cause unnecessary blinking, resizing, re-layout effects because it's annoying
;; 3. In case we don't know how long operation will take we use spinners, otherwise - progress bars
;; 4. We don't really want to annoy user with spinners too much (especially if he/she is on good connection)
;;    so we should add delays before showing large spinners or overlay spinners
;; 5. On the other hand we still want to indicate that network operation completed successfully even
;;    if on-screen result haven't changed and even if it was really quick, so it's a good idea to show
;;    small absolute-positioned spinner somewhere on screen so user can see that system is working
;;
;; Quickest approach - show delayed inline spinner, delay should be correlated with debounce interval
;; (if applicable) default delay is 200ms. Inline spinner appearance is animated with fade-in effect as well
;;
;; Adjust as necessary for different use-cases
;;

;; Design system sizes in px: modal content, tables/grids, buttons/fields
(def spinner-sizes
  {:modal 20
   :table 28
   :text  11})

(def stroke-width 2)

;; Visible part of the circle, the rest is a gap
(def arc-ratio 0.75)

(defn- size->px
  [size]
  (let [size (if (string? size)
               (keyword size)
               size)]
    (get spinner-sizes size (:table spinner-sizes))))

(defn ^{:component? true}
  mux-spinner
  "Spinner component with MouseUX style.

  * `delay` - delay in milliseconds before showing component, delay starts after component is mounted
  * `size` - `:modal` / `:table` / `:text` (see `spinner-sizes`)
  * `bezier?` - use bezier animation function
  * `fade-in?` - do a fade-in animation when mounting spinner
  * `with-overlay?` - create overlay for spinner, must have position: relative/absolute parent
  * `color` - `:primary` (default) or `:inverted` (white, for filled buttons)"
  [{:keys [delay size bezier? fade-in? with-overlay? color]
    :or {size :table
         color :primary
         delay 0
         fade-in? true
         bezier? false
         with-overlay? false}}]
  [delayed-after-mount
   delay
   (fn [delay-finished]
     (let [size-px (size->px size)
           center (/ size-px 2)
           radius (/ (- size-px stroke-width) 2)
           circumference (* 2 js/Math.PI radius)]
       (when delay-finished
         [:<>
          (when with-overlay?
            [:div.spinner-overlay])

          (let [spinner
                [:div.new-spinner-holder
                 {:class (dom/classes-pairs-v :spinner-fade-in fade-in?
                                              :inverted (= "inverted" (name color)))}
                 [:svg.new-spinner
                  {:class   (dom/classes-pairs-v :bezier bezier?)
                   :viewBox (str "0 0 " size-px " " size-px)
                   :width   size-px
                   :height  size-px
                   :xmlns   "http://www.w3.org/2000/svg"
                   :fill    "none"
                   :role    "status"}
                  [:circle
                   {:cx               center
                    :cy               center
                    :r                radius
                    :stroke-width     stroke-width
                    :stroke-dasharray (str (* arc-ratio circumference) " " circumference)}]]]]
            (if with-overlay?
              ;; Wrap with special div in overlay case
              [:div.spinner-holder
               spinner]
              ;; No overlay - just show spinner
              spinner))])))])

(defn ^{:component? true}
  spinner-with-text
  "Spinner (`:modal` or `:table` size) with a text below it, centered in the parent."
  [{:keys [size text delay]
    :or {size :table}}]
  [:div.spinner-with-text
   [mux-spinner {:size  size
                 :delay delay}]
   [:div.spinner-text
    {:class (when (and (= size :modal) (not text)) "two-lines")}
    (or text (get-translation "issue.bugtracker.modal.loading" "Loading ... Please Wait"))]])

(defn modal-spinner
  "Fullscreen modal spinner.
   Generally, it's a bad idea to have modal spinners, but we need it sometimes while transitioning from MPA to SPA"
  [{:keys [show? body]}]
  (when @show?
    [:div.fullscreen-spinner-backdrop body]))

(development-only
  (defonce examples-state (reagent/atom {}))

  (defn spinners-section
    [header items]
    [:<>
     [:h4 header]
     [:div.spinner-examples-row
      (for [item items]
        [:div.spinner-examples-item
         {:key (:key item)}
         [:span (:title item)]
         (:component item)])]])

  (defn ^{:component-example mux-spinner}
    simple-spinner-examples
    []
    [:div
     [spinners-section
      "Design system sizes"
      (for [size (keys spinner-sizes)]
        {:key       (str size)
         :title     (str size)
         :component [mux-spinner {:size size}]})]

     [spinners-section
      "Spinner with text"
      [{:key       "modal"
        :title     ":modal"
        :component [spinner-with-text {:size :modal
                                       :text "Generating Steps .... Please wait"}]}
       {:key       "table"
        :title     ":table"
        :component [spinner-with-text {:size :table}]}]]

     [:h3 "TypeScript spinner for comparison"]
     [:div
      [:r> Loading]]

     [:h3 "Spinner with delay"]

     [:p "All spinners have fade-in delay by default, but you can provide additional delay as
     a property if you want to delay showing of spinner more"]

     [:label
      [:input
       {:type      "checkbox"
        :checked   (boolean (:toggle @examples-state))
        :on-change (fn [_]
                     (swap! examples-state update :toggle not))}]
      " Toggle"]

     [:h4 "Spinner without extra delay"]

     (when (:toggle @examples-state)
       (example
         [mux-spinner]))

     [:h4 "Spinner with extra 1000ms delay"]

     (when (:toggle @examples-state)
       (example
         [mux-spinner {:delay 1000}]))

     [:h4 "Spinner with overlay"]

     [:p "You can use spinner with overlay if you want to disable some content indicating
     progress, but don't want to clear screen every time. Note that it requires
     position: relative/absolute parent to position overlay correctly"]

     [:div.spinner-examples-overlay-box
      (when (:toggle @examples-state)
        (example [mux-spinner {:with-overlay? true
                               :size          :modal}]))
      "Div with some content"]]))

(register-components!)
